import React, {createContext, useContext} from 'react';
import {Form, FormikProvider, useFormik} from 'formik';
import {useSelector, shallowEqual} from 'react-redux';
import {useIntl} from 'react-intl';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {RootState} from '../../../../store/configureStore';


export type InviteFriendContext = {[key: string]: unknown}

export type InviteFriendContextProviderProps = {
  children: React.ReactNode,
}

export const defaultContext: InviteFriendContext = {};

export type InviteFriendFromValues = {
  name: string,
  phone: string,
}

export const defaultFormValues: InviteFriendFromValues = {
  name: '',
  phone: '',
};

export const InviteFriendContext = createContext<InviteFriendContext>(defaultContext);
InviteFriendContext.displayName = 'InviteFriendContext';

export const useInviteFriendContext = () => useContext(InviteFriendContext);

export const InviteFriendContextProvider = ({children}: InviteFriendContextProviderProps) => {
  const intl = useIntl();
  const textFieldRequired = intl.formatMessage({
    id: 'message-error-requiredField',
    defaultMessage: 'Required field'
  });

  const currentLocale = useSelector((state: RootState) => state.i18n.locale, shallowEqual);
  const {apiService} = useGlobalContext();
  const globalMessage = useGlobalMessageContext();
  const M = useMessage();

  const formik = useFormik<InviteFriendFromValues>({
    initialValues: {...defaultFormValues},
    validate: (values) => {
      const errors: Partial<{ [key in keyof InviteFriendFromValues]: string }> = {};
      if (!values.name) errors.name = textFieldRequired;
      if (!values.phone) errors.phone = textFieldRequired;

      return errors;
    },
    onSubmit: async (values) => {
      try {
        const result = await apiService.inviteFriend(values.name, values.phone, currentLocale);
        if (result.errorCode === 0) {
          globalMessage.open(M.inviteSuccess);
        }
        if (result.errorCode < 0) {
          globalMessage.open(M.inviteError);
        }
      } catch (e) {
        globalMessage.open(M.serverError)
      }
    },
    enableReinitialize: true,
  });

  const context: InviteFriendContext = {};

  return (
    <InviteFriendContext.Provider value={context}>
      <FormikProvider value={formik}>
        <Form>
          {children}
        </Form>
      </FormikProvider>
    </InviteFriendContext.Provider>
  );
};
